"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { useRouter, useSearchParams } from "next/navigation";
import { useAuth } from "../../../lib/auth/AuthContext";
import Header from "../../components/Header";

const formVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
};

export default function LoginPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    user,
    loading: authLoading,
    signInWithGoogle,
    refreshSession,
  } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [error, setError] = useState(searchParams.get("error") || "");
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    phone: "",
    rememberMe: false,
  });

  useEffect(() => {
    if (!authLoading && user) {
      console.log("Login page: User already logged in, redirecting...");
      const to = searchParams.get("redirect") || "/";
      router.replace(to);
    }
  }, [user, authLoading, router, searchParams]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setFormLoading(true);

    if (!isLogin) {
      // Registration logic
      if (formData.password !== formData.confirmPassword) {
        setError("Passwords do not match");
        setFormLoading(false);
        return;
      }
      try {
        const res = await fetch("/api/register", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: `+94${formData.phone}`,
            password: formData.password,
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          setError(data.error || "Registration failed");
        } else {
          // Registration success: redirect to login
          setIsLogin(true);
          setError("Registration successful. Please log in.");
          setFormData((prev) => ({
            ...prev,
            password: "",
            confirmPassword: "",
          }));
        }
      } catch {
        // Catch block for registration API call - removed unused 'err'
        setError("Registration failed. Please try again.");
      } finally {
        setFormLoading(false);
      }
      return;
    }

    // Logic for handling login
    try {
      // Call the backend API route for login
      const response = await fetch("/api/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        // Handle errors from the API (e.g., invalid credentials)
        setError(
          result.error || "Login failed. Please check your credentials."
        );
      } else {
        // Login successful via API
        console.log("API Login successful:", result.user);
        // Explicitly refresh session in context AFTER successful API call
        await refreshSession();
        // Redirect after successful API login and session refresh
        const to = searchParams.get("redirect") || "/";
        console.log(`Redirecting to: ${to}`);
        router.push(to);
      }
    } catch (err) {
      // Handle network errors or unexpected issues during fetch
      console.error("API Login request failed:", err);
      setError(
        "Login failed due to a network or server issue. Please try again."
      );
    } finally {
      setFormLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setError("");
      setFormLoading(true);
      console.log("Initiating Google sign-in from login page");
      await signInWithGoogle();
      // Note: We won't reach this point as the user will be redirected to Google's OAuth page
    } catch (err) {
      console.error("Google sign-in error:", err);
      const errorMessage =
        err instanceof Error ? err.message : "An unknown error occurred";
      setError(
        errorMessage || "Failed to sign in with Google. Please try again."
      );
      setFormLoading(false);
    }
  };

  const toggleForm = () => {
    setIsLogin(!isLogin);
  };

  if (authLoading || user) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      <main className="flex-grow pt-16">
        {/* Hero Section with Gradient Text */}
        <div className="w-full h-[200px] flex items-center justify-center bg-gradient-to-b from-gray-100 to-white">
          <AnimatePresence mode="wait">
            <motion.h1
              key={isLogin ? "login-title" : "register-title"}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5 }}
              className="text-[80px] mt-10 font-bold text-transparent bg-clip-text uppercase tracking-wider"
              style={{
                backgroundImage:
                  "linear-gradient(180deg, #000000 0%, #DBDBDB 72.42%, rgba(219, 219, 219, 0.00) 100%)",
              }}
            >
              {isLogin ? "LOGIN" : "REGISTER"}
            </motion.h1>
          </AnimatePresence>
        </div>

        <div className="container mx-auto px-4 py-12 max-w-md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white/90 backdrop-blur-lg rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] overflow-hidden border border-gray-100"
          >
            {/* Form Tabs */}
            <div className="flex">
              <button
                onClick={() => setIsLogin(true)}
                className={`flex-1 py-6 text-center font-bold text-lg transition-all ${
                  isLogin
                    ? "text-[#FF0000] border-b-2 border-[#FF0000] bg-white"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                LOGIN
              </button>
              <button
                onClick={() => setIsLogin(false)}
                className={`flex-1 py-6 text-center font-bold text-lg transition-all ${
                  !isLogin
                    ? "text-[#FF0000] border-b-2 border-[#FF0000] bg-white"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                REGISTER
              </button>
            </div>

            {/* Form Content */}
            <div className="p-12 relative overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.form
                  key={isLogin ? "login" : "register"}
                  onSubmit={handleSubmit}
                  className="space-y-6"
                  variants={formVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  transition={{ duration: 0.3 }}
                >
                  {/* Registration Fields */}
                  {!isLogin && (
                    <>
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="grid grid-cols-2 gap-4"
                      >
                        <div>
                          <label
                            htmlFor="firstName"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            First Name
                          </label>
                          <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            className="w-full px-0 py-3 bg-transparent border-b-2 border-gray-200 focus:outline-none focus:border-[#FF0000] transition-all text-lg text-black"
                            required={!isLogin}
                            placeholder="John"
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="lastName"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            Last Name
                          </label>
                          <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            className="w-full px-0 py-3 bg-transparent border-b-2 border-gray-200 focus:outline-none focus:border-[#FF0000] transition-all text-lg text-black"
                            required={!isLogin}
                            placeholder="Doe"
                          />
                        </div>
                      </motion.div>
                      {/* Phone Number Field */}
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                      >
                        <label
                          htmlFor="phone"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Phone Number
                        </label>
                        <div className="relative flex items-center">
                          <span className="absolute left-3 text-lg text-gray-500 pointer-events-none select-none">
                            +94
                          </span>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="w-full pl-14 pr-2 py-3 bg-transparent border-b-2 border-gray-200 focus:outline-none focus:border-[#FF0000] transition-all text-lg rounded text-black"
                            required={!isLogin}
                            pattern="[0-9]{9,10}"
                            maxLength={10}
                            placeholder="771234567"
                            inputMode="numeric"
                            style={{ letterSpacing: "0.05em" }}
                          />
                        </div>
                      </motion.div>
                    </>
                  )}

                  {/* Email Field */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-0 py-3 bg-transparent border-b-2 border-gray-200 focus:outline-none focus:border-[#FF0000] transition-all text-lg text-black"
                      required
                      placeholder="your@email.com"
                    />
                  </div>

                  {/* Password Field */}
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full px-0 py-3 bg-transparent border-b-2 border-gray-200 focus:outline-none focus:border-[#FF0000] transition-all text-lg text-black"
                      required
                      placeholder="••••••••"
                    />
                  </div>

                  {/* Confirm Password Field (Register only) */}
                  {!isLogin && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <label
                        htmlFor="confirmPassword"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Confirm Password
                      </label>
                      <input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className="w-full px-0 py-3 bg-transparent border-b-2 border-gray-200 focus:outline-none focus:border-[#FF0000] transition-all text-lg text-black"
                        required={!isLogin}
                        placeholder="••••••••"
                      />
                    </motion.div>
                  )}

                  {/* Remember Me Checkbox */}
                  {!isLogin && (
                    <div className="flex items-center">
                      <label
                        htmlFor="rememberMe"
                        className="text-sm text-gray-700 mr-2"
                      >
                        Remember Me
                      </label>
                      <Link
                        href="/forgot-password"
                        className="text-sm text-[#FF0000] hover:underline"
                      >
                        Forgot password?
                      </Link>
                    </div>
                  )}

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    disabled={formLoading}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-[#FF0000] text-white font-bold py-3 px-4 rounded-full focus:outline-none focus:shadow-outline transition-colors hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {formLoading
                      ? "Processing..."
                      : isLogin
                      ? "LOGIN"
                      : "REGISTER"}
                  </motion.button>

                  {/* Error Message */}
                  {error && (
                    <p className="text-red-500 text-sm text-center mt-4">
                      {error}
                    </p>
                  )}

                  {/* Separator */}
                  <div className="flex items-center my-8">
                    <hr className="flex-grow border-gray-300" />
                    <span className="mx-4 text-gray-400 font-semibold text-sm">
                      OR
                    </span>
                    <hr className="flex-grow border-gray-300" />
                  </div>

                  {/* Google Sign-In Button */}
                  <motion.button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={formLoading}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full flex items-center justify-center bg-white border border-gray-300 text-gray-700 font-medium py-3 px-4 rounded-full focus:outline-none focus:shadow-outline transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Image
                      src="/google.svg"
                      alt="Google Icon"
                      width={20}
                      height={20}
                      className="mr-3"
                    />
                    Continue with Google
                  </motion.button>

                  {/* Toggle Form Link */}
                  <p className="text-center text-sm text-gray-600 mt-8">
                    {isLogin
                      ? "Don't have an account?"
                      : "Already have an account?"}{" "}
                    <button
                      type="button"
                      onClick={toggleForm}
                      className="font-medium text-[#FF0000] hover:underline"
                    >
                      {isLogin ? "Register Now" : "Login Here"}
                    </button>
                  </p>
                </motion.form>
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
